import React, { useContext, useEffect, useState } from 'react';
import { SetupContext } from '../../Context/SetupContext';
import css from './style.module.css';

export default function ManageFields({ objKey, editIndex, setEditIndex }) {
    const { manageFields, updateField, deleteField, addField } = useContext(SetupContext);
    const [field, setField] = useState([]);
    const [editValue, setEditValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [disabledFields, setDisabledFields] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [newFieldValue, setNewFieldValue] = useState('');

    useEffect(() => {
        if (manageFields[objKey]?.length > 0) {
            setField(manageFields[objKey]);
        } else {
            setField([]);
        }
    }, [manageFields, objKey]);

    const getDisplayValue = (item) => {
        switch (objKey) {
            case 'types': return item.type;
            case 'channels': return item.channel;
            case 'geographies': return item.geography;
            case 'priorities': return item.priority;
            default: return '';
        }
    };

    const handleKeyDown = async (e, id) => {
        if (e.key === 'Enter') {
            setLoading(true);
            await updateField(objKey, id, editValue);
            setEditIndex(null);
            setLoading(false);
        } else if (e.key === 'Escape') {
            setEditIndex(null);
        }
    };

    const updateFunc = async (objKey, id, updateValue) => {
        if (updateValue.trim() === '') return;
        setLoading(true);
        await updateField(objKey, id, updateValue);
        setEditIndex(null);
        setLoading(false);
    };

    const deleteFunc = async (objKey, id) => {
        await deleteField(objKey, id);
        setDisabledFields(prev => {
            const newState = { ...prev };
            delete newState[id];
            return newState;
        });
    };

    const addNewField = async () => {
        if (newFieldValue.trim() === '') return;
        setLoading(true);
        await addField(objKey, newFieldValue);
        setNewFieldValue('');
        setLoading(false);
    };

    const filteredFields = field.filter(item =>
        getDisplayValue(item)?.toLowerCase()?.includes(searchTerm.toLowerCase())
    );

    return (
        <div className="container-fluid">
            {/* Controls Row - Responsive Layout */}
            <div className="row g-3 mb-4">
                {/* Search Section */}
                <div className="col-12 col-md-5">
                    <div className="input-group">
                        <input
                            type="text"
                            className="form-control form-control-sm inputColor"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <span className="input-group-text inputColor">
                            <i className="fa-solid fa-magnifying-glass"></i>
                        </span>
                    </div>
                </div>

                {/* Add New Field Section */}
                <div className="col-12 col-md-7">
                    <div className="d-flex gap-2">
                        <input
                            type="text"
                            className={`form-control form-control-sm inputColor`}
                            placeholder="New Option"
                            value={newFieldValue}
                            onChange={(e) => setNewFieldValue(e.target.value)}
                        />
                        <button 
                            className="btn btn-sm btnCustom flex-shrink-0" 
                            onClick={addNewField}
                            disabled={loading}
                        >
                            <span className="d-none d-sm-inline">Add Option</span>
                            <span className="d-sm-none">Add</span>
                            {loading && <i className="fa-solid fa-circle-notch fa-spin ms-2"></i>}
                        </button>
                    </div>
                </div>
            </div>

            {/* Fields Grid - Responsive Cards */}
            <div className="row g-3">
                {field.length === 0 ? (
                    <div className="col-12">
                        <div className={`${css.emptyState} text-center py-5`}>
                            <i className="fa-solid fa-folder-open fa-3x mb-3 text-muted"></i>
                            <p className="text-muted fs-5">No {objKey} found</p>
                        </div>
                    </div>
                ) : (
                    filteredFields.map((item, index) => (
                        <div key={index} className="col-12 col-sm-6 col-md-4 col-lg-3">
                            <div className={`${css.field} ${disabledFields[item.Id] ? css.disabledDiv : ""} ${editIndex === index ? css.fieldEditing : ''}`}>
                                {/* Field Content */}
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    {editIndex === index ? (
                                        <input
                                            type="text"
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            onKeyDown={(e) => handleKeyDown(e, item.Id)}
                                            className={`form-control form-control-sm ${css.editInput}`}
                                            autoFocus
                                        />
                                    ) : (
                                        <p className="mb-0 fw-medium text-break">{getDisplayValue(item)}</p>
                                    )}
                                    
                                    {/* Action Buttons */}
                                    <div className="d-flex gap-2 flex-shrink-0 ms-2">
                                        <button
                                            className={`btn btn-sm ${css.actionBtn} ${css.editBtn}`}
                                            onClick={() => {
                                                setEditIndex(index);
                                                setEditValue(getDisplayValue(item));
                                            }}
                                            disabled={disabledFields[item.Id]}
                                        >
                                            <i className="fa-solid fa-pen"></i>
                                        </button>
                                        
                                        {disabledFields[item.Id] ? (
                                            <button className={`btn btn-sm ${css.actionBtn}`} disabled>
                                                <i className="fa-solid fa-circle-notch fa-spin"></i>
                                            </button>
                                        ) : (
                                            <button
                                                className={`btn btn-sm ${css.actionBtn} ${css.deleteBtn}`}
                                                onClick={() => {
                                                    setDisabledFields(prev => ({
                                                        ...prev,
                                                        [item.Id]: true
                                                    }));
                                                    deleteFunc(objKey, item.Id);
                                                }}
                                            >
                                                <i className="fa-solid fa-trash"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Edit Controls */}
                                {editIndex === index && (
                                    <div className="d-flex gap-2 mt-3">
                                        <button
                                            className="btn btn-success btn-sm flex-fill"
                                            onClick={async () => {
                                                await updateFunc(objKey, item.Id, editValue);
                                            }}
                                            disabled={loading}
                                        >
                                            <i className="fa-solid fa-check me-1"></i>
                                            Save
                                            {loading && <i className="fa-solid fa-circle-notch fa-spin ms-2"></i>}
                                        </button>
                                        <button 
                                            className="btn btn-outline-secondary btn-sm flex-fill" 
                                            onClick={() => setEditIndex(null)}
                                        >
                                            <i className="fa-solid fa-times me-1"></i>
                                            Cancel
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}